import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Breadcrumbs } from "@/components/molecules/Breadcrumbs";
import { useDrawerPageTitle, usePageBreadcrumbs } from "@/context/PageNavigation";
import { IconButton } from "@/components/atoms/IconButton";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  width?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  marginLeft?: string | number;
  /** When provided, replaces the close X with a back arrow on the left. */
  onBack?: () => void;
  /** Rendered in the header's top-right, alongside the close button. */
  headerAside?: ReactNode;
}

const WIDTHS = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
  "2xl": "max-w-7xl",
  full: "max-w-full",
} as const;

export function Drawer({ open, onClose, title, subtitle, children, actions, width = "md", marginLeft, onBack, headerAside }: Props) {
  useDrawerPageTitle(open, title);
  const pageBreadcrumbs = usePageBreadcrumbs();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const breadcrumbs = [
    ...pageBreadcrumbs.map((item, index) => index === pageBreadcrumbs.length - 1
      ? { label: item.label || "", onClick: onBack ?? onClose }
      : item),
    ...(title ? [{ label: title }] : []),
  ];

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end mt-14" style={marginLeft != null ? { marginLeft } : undefined}>
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fadeIn"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className={cn(
          "relative h-full w-full bg-bridge-surface/95 border-l border-bridge-border/70",
          "shadow-glass animate-slideIn flex flex-col",
          WIDTHS[width],
        )}
        role="dialog"
        aria-label={title}
      >
        <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-bridge-border/60">
          <div className="flex items-start gap-3 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Go back"
                className="mt-0.5 p-2 -ml-2 rounded-md text-bridge-muted hover:text-bridge-fg hover:bg-bridge-surface/80 transition-colors shrink-0"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div className="min-w-0">
              {pageBreadcrumbs.length > 0 && <div className="mb-1"><Breadcrumbs items={breadcrumbs} /></div>}
              <h2 className="text-lg font-semibold text-bridge-fg">{title}</h2>
              {subtitle && <div className="mt-0.5 text-xs text-bridge-muted">{subtitle}</div>}
            </div>
          </div>
          <div className="flex items-start gap-3 shrink-0">
            {headerAside}
            {!onBack && (
              <IconButton label="Close" onClick={onClose}>
                <X size={16} />
              </IconButton>
            )}
          </div>
        </header>
        <div className="flex-1 overflow-auto px-6 py-5">{children}</div>
        {actions && <footer className="px-6 py-3 border-t border-bridge-border/60 flex justify-end gap-2">{actions}</footer>}
      </aside>
    </div>,
    document.body,
  );
}
